import { Form } from 'antd';
import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    CKEDITOR: any;
    CKEDITOR_BASEPATH?: string;
  }
}

interface IProps {
  input: any;
  idComponent: string;
  placeholder?: string;
  isMounted?: boolean;
  meta: any;
  initValues?: any;
  label?: string;
  disabled?: boolean;
  className?: string;
}


export const InputRichText = (props: IProps) => {
  const {
    input,
    meta: { error },
    idComponent,
    label,
    disabled = false,
    className = '',
    initValues,
  } = props;

  const editorInstance = useRef<any>(null);
  const containerRef = useRef<HTMLTextAreaElement>(null);
  const currentValue = initValues !== undefined ? initValues : input?.value || '';
  const currentValueRef = useRef(currentValue);
  currentValueRef.current = currentValue;

  const inputRef = useRef(input);
  inputRef.current = input;

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    let isCancelled = false;

    const initEditor = () => {
      if (!window.CKEDITOR || !containerRef.current || isCancelled) return;

      if (window.CKEDITOR.instances[idComponent]) {
        try {
          window.CKEDITOR.instances[idComponent].destroy(true);
        } catch (e) {
          // ignore if already destroyed
        }
      }

      const editor = window.CKEDITOR.replace(containerRef.current, {
        versionCheck: false,
        readOnly: disabledRef.current,
        contentsCss: ['/ckeditor/contents.css'],
      });

      editorInstance.current = editor;

      editor.on('instanceReady', () => {
        if (isCancelled) return;
        if (currentValueRef.current) {
          editor.setData(currentValueRef.current);
        }
        if (disabledRef.current) {
          editor.setReadOnly(true);
        }
      });

      editor.on('change', () => {
        if (isCancelled) return;
        const data = editor.getData();
        inputRef.current.onChange(data);
      });

      editor.on('blur', () => {
        if (inputRef.current && inputRef.current.onBlur) {
          inputRef.current.onBlur();
        }
      });
    };

    initEditor();

    return () => {
      isCancelled = true;
      if (editorInstance.current) {
        try {
          editorInstance.current.destroy(true);
        } catch (e) {
          // ignore on destroy
        }
        editorInstance.current = null;
      }
    };
  }, [idComponent]);

  useEffect(() => {
    const editor = editorInstance.current;
    if (editor && editor.instanceReady) {
      if (editor.getData() !== currentValue) {
        editor.setData(currentValue || '');
      }
    }
  }, [currentValue]);

  useEffect(() => {
    const editor = editorInstance.current;
    if (editor && editor.instanceReady) {
      editor.setReadOnly(disabled);
    }
  }, [disabled]);

  const labelTitle = label ? (
    <span>
      <b className={`capital ${className}`}>{label}</b>
    </span>
  ) : null;

  return (
    <Form.Item
      validateStatus={error !== undefined ? 'error' : ''}
      help={error !== undefined ? error : ''}
      label={labelTitle}
      colon={false}
    >
      <textarea
        ref={containerRef}
        id={idComponent}
        defaultValue={currentValue}
      />
    </Form.Item>
  );
};

